import { useEffect, useMemo, useState } from 'react'
import { Plus, X } from 'lucide-react'
import type { DayFilter, MarketEvent, Schedule, ScheduleKind } from '@shared/schedule'
import { epochToLocal, tzShort } from '@shared/schedule'
import type { SchedulePreview } from '@shared/types'
import { api } from '../../lib/api'
import { cn } from '../../lib/utils'

/* ── small helpers ──────────────────────────────────────────────────── */

/** "in 45s" / "in 3h 12m" / "in 2d 4h" (or "now"). */
export function untilText(ms: number, now = Date.now()): string {
  const s = Math.round((ms - now) / 1000)
  if (s <= 0) return 'now'
  if (s < 60) return `in ${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `in ${m}m${s % 60 && m < 10 ? ` ${s % 60}s` : ''}`
  const h = Math.floor(m / 60)
  if (h < 48) return `in ${h}h${m % 60 ? ` ${m % 60}m` : ''}`
  const d = Math.floor(h / 24)
  return `in ${d}d${h % 24 ? ` ${h % 24}h` : ''}`
}

const WD_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
/** "Mon 29 Sep 09:25:30 ET" in a zone (seconds only when not :00). */
export function runText(ms: number, tz: string): string {
  const l = epochToLocal(ms, tz)
  const d = new Date(`${l.slice(0, 10)}T12:00:00Z`)
  const time = l.endsWith(':00') ? l.slice(11, 16) : l.slice(11, 19)
  return `${WD_SHORT[d.getUTCDay()]} ${d.getUTCDate()} ${d.toLocaleString('en', { month: 'short', timeZone: 'UTC' })} ${time} ${tzShort(tz)}`
}
/** The same moment in the other zone the user thinks in (ET ↔ Turkey). */
export const otherTz = (tz: string) => (tz === 'Europe/Istanbul' ? 'America/New_York' : 'Europe/Istanbul')

/** Re-render every second while mounted (countdowns). */
export function useNow(ms = 1000): number {
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms)
    return () => clearInterval(t)
  }, [ms])
  return now
}

/* ── the form's draft ⇄ a schedule ─────────────────────────────────────── */

export interface ScheduleDraft {
  kind: ScheduleKind
  at: string[]
  every: number
  unit: 's' | 'm' | 'h' | 'd'
  window: 'always' | 'session' | 'extended' | 'custom'
  winFrom: string
  winTo: string
  times: string[]
  weekdays: number[]
  monthMode: 'days' | 'nth'
  monthDays: number[]
  nthN: number
  nthWd: number
  months: number[]
  dates: string[]
  event: MarketEvent
  offsetMin: number
  cron: string
  tz: string
  days: DayFilter
  start: string
  end: string
  maxRuns: string
}

const UNIT_SEC = { s: 1, m: 60, h: 3600, d: 86_400 } as const

/** A local "YYYY-MM-DDTHH:MM" a few minutes from now in `tz` (a new one-off moment). */
function soon(tz: string, minutes = 10): string {
  return epochToLocal(Date.now() + minutes * 60_000, tz).slice(0, 16).replace(' ', 'T')
}

export function newDraft(tz = 'America/New_York'): ScheduleDraft {
  return {
    kind: 'daily',
    at: [soon(tz)],
    every: 5,
    unit: 'm',
    window: 'session',
    winFrom: '09:30',
    winTo: '16:00',
    times: ['09:25'],
    weekdays: [1],
    monthMode: 'days',
    monthDays: [1],
    nthN: 1,
    nthWd: 5,
    months: [],
    dates: ['01-01'],
    event: 'open',
    offsetMin: 0,
    cron: '0 */15 9-16 * * 1-5',
    tz,
    days: 'all',
    start: '',
    end: '',
    maxRuns: '',
  }
}

export function draftFromSchedule(s: Schedule): ScheduleDraft {
  const d = newDraft(s.tz)
  d.kind = s.kind
  d.days = s.days
  d.start = s.start?.replace(' ', 'T') ?? ''
  d.end = s.end?.replace(' ', 'T') ?? ''
  d.maxRuns = s.maxRuns ? String(s.maxRuns) : ''
  if (s.at) d.at = s.at.map((a) => a.replace(' ', 'T'))
  if (s.every) {
    const unit = s.every % 86_400 === 0 ? 'd' : s.every % 3600 === 0 ? 'h' : s.every % 60 === 0 ? 'm' : 's'
    d.unit = unit
    d.every = s.every / UNIT_SEC[unit]
  }
  if (s.window) {
    if (typeof s.window === 'string') d.window = s.window
    else {
      d.window = 'custom'
      d.winFrom = s.window.from
      d.winTo = s.window.to
    }
  } else d.window = 'always'
  if (s.times) d.times = s.times
  if (s.weekdays) d.weekdays = s.weekdays
  if (s.nth) {
    d.monthMode = 'nth'
    d.nthN = s.nth.n
    d.nthWd = s.nth.weekday
  }
  if (s.monthDays) d.monthDays = s.monthDays
  if (s.months) d.months = s.months
  if (s.dates) d.dates = s.dates
  if (s.event) d.event = s.event
  if (s.offsetMin != null) d.offsetMin = s.offsetMin
  if (s.cron) d.cron = s.cron
  return d
}

/** The draft as the loose schedule the server checks (only the chosen kind's fields). */
export function scheduleFromDraft(d: ScheduleDraft): Record<string, unknown> {
  const out: Record<string, unknown> = { kind: d.kind, tz: d.tz }
  if (d.kind !== 'once' && d.kind !== 'market' && d.days !== 'all') out.days = d.days
  if (d.kind !== 'once') {
    if (d.start) out.start = d.start
    if (d.end) out.end = d.end
    if (d.maxRuns && Number(d.maxRuns) > 0) out.maxRuns = Math.round(Number(d.maxRuns))
  }
  const win = d.window === 'always' ? undefined : d.window === 'custom' ? { from: d.winFrom, to: d.winTo } : d.window
  switch (d.kind) {
    case 'once':
      out.at = d.at.filter(Boolean)
      break
    case 'interval':
      out.every = Math.max(1, Math.round(d.every * UNIT_SEC[d.unit]))
      if (win) out.window = win
      break
    case 'daily':
      out.times = d.times.filter(Boolean)
      break
    case 'weekly':
      out.weekdays = d.weekdays
      out.times = d.times.filter(Boolean)
      break
    case 'monthly':
      if (d.monthMode === 'days') out.monthDays = d.monthDays
      else out.nth = { n: d.nthN, weekday: d.nthWd }
      if (d.months.length) out.months = d.months
      out.times = d.times.filter(Boolean)
      break
    case 'yearly':
      out.dates = d.dates.filter(Boolean)
      out.times = d.times.filter(Boolean)
      break
    case 'market':
      out.event = d.event
      out.offsetMin = d.offsetMin
      break
    case 'cron':
      out.cron = d.cron
      if (win) out.window = win
      break
  }
  return out
}

/* ── pieces ─────────────────────────────────────────────────────────────── */

export const field =
  'rounded-lg border border-[var(--line-soft)] bg-[var(--color-inset)] px-2.5 py-1.5 text-[12.5px] text-fg focus:border-[var(--line)] focus:outline-none'

export function Segmented<T extends string>({ value, options, onChange, small }: { value: T; options: { value: T; label: string }[]; onChange: (v: T) => void; small?: boolean }) {
  return (
    <div className="inline-flex flex-wrap gap-0.5 rounded-lg bg-[var(--color-inset)] p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded-md font-medium transition-colors',
            small ? 'px-2 py-1 text-[11.5px]' : 'px-2.5 py-1.5 text-[12px]',
            value === o.value ? 'bg-[var(--color-surface-3)] text-fg shadow-[var(--e-1)]' : 'text-fg-subtle hover:text-fg',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function Chip({ on, children, onClick, title }: { on: boolean; children: React.ReactNode; onClick: () => void; title?: string }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={cn(
        'num min-w-[30px] rounded-md border px-1.5 py-1 text-[11.5px] transition-colors',
        on ? 'border-[var(--line)] bg-[var(--color-surface-3)] text-fg' : 'border-[var(--line-soft)] text-fg-subtle hover:text-fg',
      )}
    >
      {children}
    </button>
  )
}

/** A list of values with add / remove (times, moments, yearly dates). */
function ListInput({ values, onChange, render, add }: { values: string[]; onChange: (v: string[]) => void; render: (v: string, set: (x: string) => void) => React.ReactNode; add: () => string }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {values.map((v, i) => (
        <span key={i} className="inline-flex items-center gap-0.5">
          {render(v, (x) => onChange(values.map((y, j) => (j === i ? x : y))))}
          {values.length > 1 && (
            <button type="button" onClick={() => onChange(values.filter((_, j) => j !== i))} className="rounded p-0.5 text-fg-faint hover:text-fg" title="Remove">
              <X size={12} />
            </button>
          )}
        </span>
      ))}
      <button type="button" onClick={() => onChange([...values, add()])} className="inline-flex items-center gap-1 rounded-md border border-dashed border-[var(--line-soft)] px-2 py-1 text-[11.5px] text-fg-subtle hover:text-fg">
        <Plus size={12} /> Add
      </button>
    </div>
  )
}

export function Row({ label, children, align = 'center' }: { label: string; children: React.ReactNode; align?: 'center' | 'start' }) {
  return (
    <div className={cn('grid grid-cols-[92px_1fr] gap-3', align === 'start' ? 'items-start' : 'items-center')}>
      <div className={cn('text-[12px] text-fg-subtle', align === 'start' && 'pt-1.5')}>{label}</div>
      <div className="min-w-0">{children}</div>
    </div>
  )
}

const KINDS: { value: ScheduleKind; label: string }[] = [
  { value: 'once', label: 'Once' },
  { value: 'interval', label: 'Every…' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'market', label: 'Market' },
  { value: 'cron', label: 'Cron' },
]
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0]
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const TZS = [
  { value: 'America/New_York', label: 'US Eastern (ET)' },
  { value: 'Europe/Istanbul', label: 'Turkey (TR)' },
  { value: 'UTC', label: 'UTC' },
]

/** The schedule part of the task form: pick a pattern, fill only what it needs. */
export function ScheduleBuilder({ d, set }: { d: ScheduleDraft; set: (patch: Partial<ScheduleDraft>) => void }) {
  const browserTz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, [])
  const tzs = TZS.some((t) => t.value === browserTz) ? TZS : [...TZS, { value: browserTz, label: browserTz }]
  const [advanced, setAdvanced] = useState(!!(d.start || d.end || d.maxRuns))
  const timeInput = (v: string, s: (x: string) => void) => <input type="time" step={1} value={v} onChange={(e) => s(e.target.value)} className={cn(field, 'num w-[108px]')} />
  const times = <ListInput values={d.times} onChange={(times) => set({ times })} render={timeInput} add={() => d.times.at(-1) ?? '09:30'} />
  const dayFilter = (
    <Segmented<DayFilter>
      small
      value={d.days}
      onChange={(days) => set({ days })}
      options={[
        { value: 'all', label: 'Every day' },
        { value: 'weekdays', label: 'Weekdays' },
        { value: 'trading', label: 'US trading days' },
      ]}
    />
  )
  const windowPick = (
    <div className="flex flex-wrap items-center gap-2">
      <Segmented
        small
        value={d.window}
        onChange={(window) => set({ window })}
        options={[
          { value: 'always', label: 'All day' },
          { value: 'session', label: 'US session' },
          { value: 'extended', label: 'Pre + after-hours' },
          { value: 'custom', label: 'Between…' },
        ]}
      />
      {d.window === 'custom' && (
        <span className="inline-flex items-center gap-1.5 text-[12px] text-fg-subtle">
          <input type="time" step={1} value={d.winFrom} onChange={(e) => set({ winFrom: e.target.value })} className={cn(field, 'num w-[104px]')} />–
          <input type="time" step={1} value={d.winTo} onChange={(e) => set({ winTo: e.target.value })} className={cn(field, 'num w-[104px]')} />
        </span>
      )}
    </div>
  )

  return (
    <div className="space-y-3">
      <Row label="Repeats">
        <Segmented value={d.kind} options={KINDS} onChange={(kind) => set({ kind })} small />
      </Row>

      {d.kind === 'once' && (
        <Row label="At" align="start">
          <ListInput
            values={d.at}
            onChange={(at) => set({ at })}
            render={(v, s) => <input type="datetime-local" step={1} value={v} onChange={(e) => s(e.target.value)} className={cn(field, 'num')} />}
            add={() => soon(d.tz, 60)}
          />
          <div className="mt-1.5 flex flex-wrap gap-1">
            {[
              ['+30 s', 0.5],
              ['+5 min', 5],
              ['+1 h', 60],
              ['+1 day', 1440],
            ].map(([label, min]) => (
              <button
                key={label as string}
                type="button"
                onClick={() => set({ at: [epochToLocal(Date.now() + (min as number) * 60_000, d.tz).replace(' ', 'T')] })}
                className="rounded-md px-1.5 py-0.5 text-[11px] text-fg-subtle hover:bg-[var(--color-surface-2)] hover:text-fg"
              >
                {label}
              </button>
            ))}
          </div>
        </Row>
      )}

      {d.kind === 'interval' && (
        <>
          <Row label="Every">
            <span className="inline-flex items-center gap-1.5">
              <input type="number" min={1} value={d.every} onChange={(e) => set({ every: Math.max(1, Number(e.target.value) || 1) })} className={cn(field, 'num w-20')} />
              <Segmented
                small
                value={d.unit}
                onChange={(unit) => set({ unit })}
                options={[
                  { value: 's', label: 'seconds' },
                  { value: 'm', label: 'minutes' },
                  { value: 'h', label: 'hours' },
                  { value: 'd', label: 'days' },
                ]}
              />
            </span>
          </Row>
          {d.unit !== 'd' && <Row label="During">{windowPick}</Row>}
          <Row label="Days">{dayFilter}</Row>
        </>
      )}

      {(d.kind === 'daily' || d.kind === 'weekly' || d.kind === 'monthly' || d.kind === 'yearly') && (
        <>
          {d.kind === 'weekly' && (
            <Row label="On">
              <div className="flex flex-wrap gap-1">
                {WEEK_ORDER.map((w) => (
                  <Chip key={w} on={d.weekdays.includes(w)} onClick={() => set({ weekdays: d.weekdays.includes(w) ? d.weekdays.filter((x) => x !== w) : [...d.weekdays, w] })}>
                    {WD_SHORT[w]}
                  </Chip>
                ))}
              </div>
            </Row>
          )}
          {d.kind === 'monthly' && (
            <>
              <Row label="On">
                <Segmented
                  small
                  value={d.monthMode}
                  onChange={(monthMode) => set({ monthMode })}
                  options={[
                    { value: 'days', label: 'Days of the month' },
                    { value: 'nth', label: 'A weekday' },
                  ]}
                />
              </Row>
              {d.monthMode === 'days' ? (
                <Row label="" align="start">
                  <div className="flex max-w-[420px] flex-wrap gap-1">
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((n) => (
                      <Chip key={n} on={d.monthDays.includes(n)} onClick={() => set({ monthDays: d.monthDays.includes(n) ? d.monthDays.filter((x) => x !== n) : [...d.monthDays, n] })}>
                        {n}
                      </Chip>
                    ))}
                    <Chip on={d.monthDays.includes(-1)} title="The month's last day" onClick={() => set({ monthDays: d.monthDays.includes(-1) ? d.monthDays.filter((x) => x !== -1) : [...d.monthDays, -1] })}>
                      Last
                    </Chip>
                  </div>
                </Row>
              ) : (
                <Row label="">
                  <span className="inline-flex items-center gap-1.5">
                    <select value={d.nthN} onChange={(e) => set({ nthN: Number(e.target.value) })} className={field}>
                      {[1, 2, 3, 4, 5, -1].map((n) => (
                        <option key={n} value={n}>
                          {n === -1 ? 'Last' : ['1st', '2nd', '3rd', '4th', '5th'][n - 1]}
                        </option>
                      ))}
                    </select>
                    <select value={d.nthWd} onChange={(e) => set({ nthWd: Number(e.target.value) })} className={field}>
                      {WEEK_ORDER.map((w) => (
                        <option key={w} value={w}>
                          {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][w]}
                        </option>
                      ))}
                    </select>
                  </span>
                </Row>
              )}
              <Row label="Months">
                <div className="flex flex-wrap items-center gap-1">
                  <Chip on={!d.months.length} onClick={() => set({ months: [] })}>
                    All
                  </Chip>
                  <Chip on={d.months.join() === '1,4,7,10'} onClick={() => set({ months: [1, 4, 7, 10] })}>
                    Quarterly
                  </Chip>
                  {MONTHS.map((m, i) => (
                    <Chip key={m} on={d.months.includes(i + 1)} onClick={() => set({ months: d.months.includes(i + 1) ? d.months.filter((x) => x !== i + 1) : [...d.months, i + 1].sort((a, b) => a - b) })}>
                      {m}
                    </Chip>
                  ))}
                </div>
              </Row>
            </>
          )}
          {d.kind === 'yearly' && (
            <Row label="On" align="start">
              <ListInput
                values={d.dates}
                onChange={(dates) => set({ dates })}
                add={() => '01-01'}
                render={(v, s) => (
                  <span className="inline-flex items-center gap-1">
                    <select value={Number(v.slice(0, 2))} onChange={(e) => s(`${String(e.target.value).padStart(2, '0')}-${v.slice(3)}`)} className={field}>
                      {MONTHS.map((m, i) => (
                        <option key={m} value={i + 1}>
                          {m}
                        </option>
                      ))}
                    </select>
                    <input type="number" min={1} max={31} value={Number(v.slice(3))} onChange={(e) => s(`${v.slice(0, 2)}-${String(Math.min(31, Math.max(1, Number(e.target.value) || 1))).padStart(2, '0')}`)} className={cn(field, 'num w-16')} />
                  </span>
                )}
              />
            </Row>
          )}
          <Row label="At" align="start">
            {times}
          </Row>
          <Row label="Days">{dayFilter}</Row>
        </>
      )}

      {d.kind === 'market' && (
        <Row label="Each session">
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <input type="number" min={0} max={720} value={Math.abs(d.offsetMin)} onChange={(e) => set({ offsetMin: Math.sign(d.offsetMin || -1) * Math.min(720, Math.abs(Number(e.target.value) || 0)) })} className={cn(field, 'num w-16')} />
            <span className="text-[12px] text-fg-subtle">min</span>
            <Segmented
              small
              value={d.offsetMin > 0 ? 'after' : d.offsetMin < 0 ? 'before' : 'at'}
              onChange={(w) => set({ offsetMin: w === 'at' ? 0 : (w === 'after' ? 1 : -1) * (Math.abs(d.offsetMin) || 15) })}
              options={[
                { value: 'before', label: 'before' },
                { value: 'at', label: 'exactly at' },
                { value: 'after', label: 'after' },
              ]}
            />
            <select value={d.event} onChange={(e) => set({ event: e.target.value as MarketEvent })} className={field}>
              <option value="premarket">pre-market start (04:00)</option>
              <option value="open">the open (09:30)</option>
              <option value="close">the close (16:00 · 13:00 early)</option>
              <option value="afterhours">after-hours end (20:00)</option>
            </select>
          </span>
          <div className="mt-1 text-[11px] text-fg-faint">US Eastern, NYSE trading days — holidays skipped, early closes followed.</div>
        </Row>
      )}

      {d.kind === 'cron' && (
        <>
          <Row label="Cron">
            <input value={d.cron} onChange={(e) => set({ cron: e.target.value })} className={cn(field, 'num w-full')} placeholder="sec min hour day month weekday" />
            <div className="mt-1 text-[11px] text-fg-faint">6 fields with seconds (or the classic 5): e.g. “0 */15 9-16 * * 1-5”.</div>
          </Row>
          <Row label="During">{windowPick}</Row>
          <Row label="Days">{dayFilter}</Row>
        </>
      )}

      {d.kind !== 'market' && (
        <Row label="Time zone">
          <select value={d.tz} onChange={(e) => set({ tz: e.target.value })} className={field}>
            {tzs.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </Row>
      )}

      {d.kind !== 'once' && (
        <div>
          <button type="button" onClick={() => setAdvanced(!advanced)} className="text-[11.5px] text-fg-subtle hover:text-fg">
            {advanced ? '− Limits' : '+ Start, end, max runs'}
          </button>
          {advanced && (
            <div className="mt-2 space-y-2">
              <Row label="Starts">
                <input type="datetime-local" step={1} value={d.start} onChange={(e) => set({ start: e.target.value })} className={cn(field, 'num')} />
              </Row>
              <Row label="Ends">
                <input type="datetime-local" step={1} value={d.end} onChange={(e) => set({ end: e.target.value })} className={cn(field, 'num')} />
              </Row>
              <Row label="Max runs">
                <input type="number" min={1} value={d.maxRuns} onChange={(e) => set({ maxRuns: e.target.value })} placeholder="no limit" className={cn(field, 'num w-28')} />
              </Row>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** The schedule in words + its next runs (both zones) + its runs a day — checked by the server as you type. */
export function SchedulePreviewBox({ schedule, created, runs, onResult }: { schedule: Record<string, unknown>; created?: number; runs?: number; onResult?: (p: SchedulePreview | null) => void }) {
  const [p, setP] = useState<SchedulePreview | null>(null)
  const key = JSON.stringify(schedule)
  useEffect(() => {
    let live = true
    const t = setTimeout(() => {
      api
        .taskPreview({ schedule, created, runs })
        .then((r) => {
          if (!live) return
          setP(r)
          onResult?.(r)
        })
        .catch(() => {})
    }, 250)
    return () => {
      live = false
      clearTimeout(t)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, created, runs])
  const tz = (schedule.tz as string) || 'America/New_York'
  if (!p) return <div className="rounded-lg bg-[var(--color-inset)] px-3 py-2 text-[12px] text-fg-faint">…</div>
  if (!p.ok) return <div className="rounded-lg border border-[color-mix(in_srgb,var(--color-down)_35%,transparent)] px-3 py-2 text-[12px] text-down">{p.errors?.join(' · ')}</div>
  return (
    <div className="rounded-lg bg-[var(--color-inset)] px-3 py-2.5">
      <div className="text-[12.5px] font-medium text-fg">{p.text}</div>
      {p.runs && p.runs.length ? (
        <div className="mt-1.5 space-y-0.5">
          {p.runs.map((r, i) => (
            <div key={r} className="num flex items-baseline justify-between gap-3 text-[11.5px]">
              <span className={i === 0 ? 'text-fg-muted' : 'text-fg-subtle'}>
                {runText(r, tz)} <span className="text-fg-faint">· {runText(r, otherTz(tz)).split(' ').slice(3).join(' ')}</span>
              </span>
              {i === 0 && <span className="shrink-0 text-fg-faint">{untilText(r)}</span>}
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-1 text-[11.5px] text-fg-faint">No upcoming runs.</div>
      )}
      {p.perDay != null && p.perDay >= 1.5 && <div className="mt-1.5 text-[11px] text-fg-faint">≈ {p.perDay} runs a day</div>}
    </div>
  )
}
